import React, { useState, useEffect, useCallback } from 'react';
import {
    TrendingDown, Search, RefreshCw, ExternalLink, CheckCircle, AlertCircle, Check, Type,
} from 'lucide-react';
import { GlassCard, GlassButton } from '../ui';
import {
    getLowCtrOpportunities, createQuickWinMetaData,
    LowCtrOpportunitiesResponse, LowCtrOpportunity,
} from '../../services/api';
import TitleRewriteModal from './TitleRewriteModal';
import './LowCtrPanel.css';

interface LowCtrPanelProps {
    projectId: string;
}

type RowStatus = 'posting' | 'done' | 'error';

/** Show the URL path (more scannable than the full origin); full URL stays on the title attr. */
const displayPage = (page: string): string => {
    try {
        const u = new URL(page);
        return (u.pathname || '/') + (u.search || '');
    } catch {
        return page;
    }
};

const LowCtrPanel: React.FC<LowCtrPanelProps> = ({ projectId }) => {
    const [data, setData] = useState<LowCtrOpportunitiesResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rowStatus, setRowStatus] = useState<Record<string, RowStatus>>({});
    // Title-rewrite modal state
    const [rewriteFor, setRewriteFor] = useState<LowCtrOpportunity | null>(null);

    const fetchOpportunities = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getLowCtrOpportunities(projectId);
            setData(result);
            setRowStatus({});
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Failed to load low-CTR opportunities.');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchOpportunities();
    }, [fetchOpportunities]);

    const handleRefreshMetadata = async (taskId: string) => {
        setRowStatus(prev => ({ ...prev, [taskId]: 'posting' }));
        try {
            await createQuickWinMetaData(taskId);
            setRowStatus(prev => ({ ...prev, [taskId]: 'done' }));
        } catch {
            setRowStatus(prev => ({ ...prev, [taskId]: 'error' }));
        }
    };

    const renderAction = (opp: LowCtrOpportunity) => {
        // No linked AIME article — still offer title rewrite (copy-only) + external link
        if (!opp.task_id) {
            return (
                <div className="lcp-action">
                    <GlassButton
                        size="sm"
                        variant="secondary"
                        onClick={() => setRewriteFor(opp)}
                        title="Rewrite this page's title using competing SERP titles"
                    >
                        <Type size={14} />
                        Rewrite title
                    </GlassButton>
                    <a
                        href={opp.page}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lcp-page-link"
                        title="This page is not linked to an AIME article — open it in a new tab"
                    >
                        <ExternalLink size={14} />
                        View page
                    </a>
                </div>
            );
        }
        const status = rowStatus[opp.task_id];
        if (status === 'done') {
            return (
                <div className="lcp-action">
                    <span className="lcp-success">
                        <Check size={14} />
                        <span>
                            Metadata refresh queued —{' '}
                            <a href={`/preview/${opp.task_id}`} target="_blank" rel="noopener noreferrer">
                                view in Content
                            </a>
                        </span>
                    </span>
                    <GlassButton
                        size="sm"
                        variant="secondary"
                        onClick={() => setRewriteFor(opp)}
                        title="Rewrite this page's title using competing SERP titles"
                    >
                        <Type size={14} />
                        Rewrite title
                    </GlassButton>
                </div>
            );
        }
        return (
            <div className="lcp-action">
                <GlassButton
                    size="sm"
                    variant="primary"
                    pill
                    disabled={status === 'posting'}
                    loading={status === 'posting'}
                    onClick={() => handleRefreshMetadata(opp.task_id!)}
                    title={opp.suggested_action || "Regenerate SEO metadata grounded in this page's top Google query"}
                >
                    {status !== 'posting' && <RefreshCw size={14} />}
                    {status === 'posting' ? 'Refreshing…' : 'Refresh metadata'}
                </GlassButton>
                <GlassButton
                    size="sm"
                    variant="secondary"
                    onClick={() => setRewriteFor(opp)}
                    title="Rewrite this page's title using competing SERP titles"
                >
                    <Type size={14} />
                    Rewrite title
                </GlassButton>
                {status === 'error' && (
                    <span className="lcp-row-error">
                        <AlertCircle size={12} />
                        Failed — try again
                    </span>
                )}
            </div>
        );
    };

    const gscConnected = data?.summary?.gsc_connected ?? false;
    const opportunities = data?.opportunities ?? [];
    const days = data?.summary?.params?.days ?? 90;

    return (
        <>
        {rewriteFor && (
            <TitleRewriteModal
                projectId={projectId}
                opportunity={rewriteFor}
                onClose={() => setRewriteFor(null)}
            />
        )}
        <GlassCard className="lcp-panel">
            <div className="lcp-header">
                <div className="lcp-title">
                    <TrendingDown size={16} />
                    <span>Low-CTR Opportunities</span>
                </div>
                <div className="lcp-header-right">
                    {data && gscConnected && (
                        <span className="lcp-summary-text">
                            {data.summary.opportunities_found} found · {data.summary.linked_to_tasks} linked to articles
                        </span>
                    )}
                    <GlassButton
                        variant="ghost"
                        size="sm"
                        onClick={fetchOpportunities}
                        disabled={loading}
                        title="Reload opportunities"
                        icon={<RefreshCw size={16} className={loading ? 'lcp-spinning' : ''} />}
                    />
                </div>
            </div>
            <p className="lcp-subtitle">
                Pages already ranking in Google that lose clicks to weak metadata — last {days} days.
            </p>

            {loading && (
                <div className="lcp-loading">
                    <RefreshCw size={16} className="lcp-spinning" />
                    <span>Loading opportunities…</span>
                </div>
            )}

            {!loading && error && (
                <div className="lcp-error">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            {!loading && !error && data && !gscConnected && (
                <div className="lcp-empty">
                    <Search size={32} className="lcp-empty-icon" />
                    <p>Connect Google Search Console to surface ranking pages that need metadata fixes.</p>
                    <p className="lcp-empty-hint">Use the Performance tab to connect your site.</p>
                </div>
            )}

            {!loading && !error && data && gscConnected && opportunities.length === 0 && (
                <div className="lcp-empty">
                    <CheckCircle size={32} className="lcp-empty-icon lcp-empty-icon--ok" />
                    <p>No underperforming pages found — metadata is healthy.</p>
                </div>
            )}

            {!loading && !error && opportunities.length > 0 && (
                <table className="seo-keyword-table lcp-table">
                    <thead>
                        <tr>
                            <th>Page</th>
                            <th style={{ textAlign: 'right' }}>Impressions</th>
                            <th style={{ textAlign: 'right' }}>CTR</th>
                            <th style={{ textAlign: 'right' }}>Position</th>
                            <th>Top Query</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {opportunities.map(opp => (
                            <tr key={opp.page}>
                                <td>
                                    <div className="lcp-page-cell" title={opp.page}>{displayPage(opp.page)}</div>
                                    {opp.task_name && (
                                        <div className="lcp-task-name" title={opp.task_name}>Article: {opp.task_name}</div>
                                    )}
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 600 }}>{opp.impressions.toLocaleString()}</td>
                                <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{(opp.ctr * 100).toFixed(1)}%</td>
                                <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{opp.position != null ? opp.position.toFixed(1) : '—'}</td>
                                <td className="lcp-query">{opp.top_query || '—'}</td>
                                <td className="lcp-action-cell">{renderAction(opp)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </GlassCard>
        </>
    );
};

export default LowCtrPanel;
